/**
 * FormatZeitleistePage — Format-Zeitleiste (Zeitscheiben über Formatversionen).
 *
 * Zeigt für einen gewählten Doctype (AHB/MIG) + Format die Folge der
 * Formatversionen als Timeline: Versionsnummer, Gültigkeitszeitraum,
 * Veröffentlichungsdatum und Badges für Fehlerkorrektur / Außerordentlich.
 *
 * Daten kommen aus `artifactsData.formatTimeline` (build-time baked,
 * kein Runtime-Fetch nötig). Einstiegspunkt: /regelwerk/zeitleiste
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Badge,
  Card,
  Notice,
  Select,
  SelectItem,
  SegmentedControl,
  Segment,
} from "@conuti-das/prince-ui";
import type { BadgeTone } from "@conuti-das/prince-ui";
import { Flex } from "@/ui-prince";
import { useRelease } from "@/context";
import artifactsData from "@/data/generated/artifacts.json";
import type { ArtifactIndex, FormatTimelineEntry } from "@/types/index";
import "./zeitleiste.css";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/** "202610" → "Oktober 2026 (FV2610)" */
function fvLabel(fv: string): string {
  const m = /^(\d{4})(\d{2})$/.exec(fv);
  if (!m) return fv;
  const month = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${month} ${m[1]} (FV${fv.slice(2)})`;
}

/** "2026-03-02" → "02.03.2026" */
function isoToDE(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

type Doctype = "ahb" | "mig";

// ---------------------------------------------------------------------------
// Dot icon for Badge (replicates StatusPill "dot" prop behaviour)
// ---------------------------------------------------------------------------

const DotIcon = () => (
  <svg width="6" height="6" viewBox="0 0 6 6" aria-hidden fill="currentColor">
    <circle cx="3" cy="3" r="3" />
  </svg>
);

// ---------------------------------------------------------------------------
// Badge component for Fehlerkorrektur / Außerordentlich / Ausgabe
// ---------------------------------------------------------------------------

function EntryBadge({ entry }: { entry: FormatTimelineEntry }) {
  if (entry.extraordinary) {
    return (
      <Badge tone={"red" as BadgeTone} icon={<DotIcon />}>
        Außerordentlich {isoToDE(entry.published_at)}
      </Badge>
    );
  }
  if (entry.error_correction) {
    return (
      <Badge tone={"orange" as BadgeTone} icon={<DotIcon />}>
        Fehlerkorrektur {isoToDE(entry.published_at)}
      </Badge>
    );
  }
  return (
    <Badge tone={"green" as BadgeTone} icon={<DotIcon />}>
      Ausgabe {isoToDE(entry.published_at)}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Timeline row
// ---------------------------------------------------------------------------

function TimelineRow({
  entry,
  isLast,
  doctype,
  format,
  current,
}: {
  entry: FormatTimelineEntry;
  isLast: boolean;
  doctype: Doctype;
  format: string;
  /** Der in der Kopfzeile gewählte Stand — hier eine Marke, keine Filterung. */
  current?: boolean;
}) {
  const targetUrl = `/regelwerk/tabellen?fv=${encodeURIComponent(entry.fv)}&format=${encodeURIComponent(format)}&doctype=${encodeURIComponent(doctype)}&mode=single`;

  return (
    <div
      className={`ztl-row${isLast ? " ztl-row--last" : ""}`}
      data-current={current ? "" : undefined}
    >
      <div className="ztl-fv-col">
        <span className="ztl-fv-label">{fvLabel(entry.fv)}</span>
        {/* Die Zeitleiste IST die FV-Achse — sie zu filtern hieße, sie
            abzuschaffen. Der Stand der Kopfzeile wird deshalb markiert,
            nicht ausgeschnitten. */}
        {current && <span className="ztl-fv-current">Kopfzeile</span>}
      </div>
      <div className="ztl-connector" aria-hidden>
        <div className="ztl-dot" />
        {!isLast && <div className="ztl-line" />}
      </div>
      <Link
        to={targetUrl}
        className="ztl-card-link"
        aria-label={`${doctype.toUpperCase()} ${format} ${fvLabel(entry.fv)} in Tabellen öffnen`}
      >
        <div className="ztl-card ztl-card--clickable">
          <div className="ztl-card-header">
            <span className="ztl-version mono">v{entry.version}</span>
            <EntryBadge entry={entry} />
          </div>
          <div className="ztl-dates">
            <span className="ztl-date-item">
              <span className="ztl-date-label">Gültig ab</span>
              <span className="ztl-date-value mono">{isoToDE(entry.valid_from)}</span>
            </span>
            <span className="ztl-date-sep">–</span>
            <span className="ztl-date-item">
              <span className="ztl-date-label">bis</span>
              <span className="ztl-date-value mono">{isoToDE(entry.valid_until)}</span>
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function FormatZeitleistePage() {
  const [params, setParams] = useSearchParams();
  // Diese Seite zeigt die Formatversions-Achse selbst; ein Filter darauf wäre
  // ihr Ende. Die globale Wahl erscheint deshalb als Marke an ihrer Zeile.
  const { release } = useRelease();

  const data = artifactsData as unknown as ArtifactIndex;
  const timeline = data.formatTimeline;

  const [doctype, setDoctype] = useState<Doctype>(
    params.get("doctype") === "mig" ? "mig" : "ahb",
  );

  // Sorted list of format names for the selected doctype
  const formats = useMemo<string[]>(() => {
    const tl = doctype === "ahb" ? timeline?.ahb : timeline?.mig;
    if (!tl) return [];
    return Object.keys(tl.formats).sort();
  }, [doctype, timeline]);

  const [format, setFormat] = useState<string>(params.get("format") ?? "");

  // Keep format valid when doctype / format list changes
  const effectiveFormat = useMemo(() => {
    if (formats.includes(format)) return format;
    return formats[0] ?? "";
  }, [format, formats]);

  // Entries for the selected format
  const entries = useMemo<FormatTimelineEntry[]>(() => {
    const tl = doctype === "ahb" ? timeline?.ahb : timeline?.mig;
    if (!tl || !effectiveFormat) return [];
    return tl.formats[effectiveFormat] ?? [];
  }, [doctype, effectiveFormat, timeline]);

  function handleDoctypeChange(keys: Set<string | number>) {
    const first = [...keys][0];
    if (first == null) return;
    const dt = String(first) as Doctype;
    setDoctype(dt);
    const next = new URLSearchParams(params);
    next.set("doctype", dt);
    next.delete("format");
    setParams(next, { replace: true });
  }

  function handleFormatChange(key: string | number | null) {
    if (key == null) return;
    const v = String(key);
    setFormat(v);
    const next = new URLSearchParams(params);
    next.set("format", v);
    setParams(next, { replace: true });
  }

  const hasTimeline = !!timeline?.ahb || !!timeline?.mig;

  return (
    <div className="page ztl-page">
      <div className="page-header">
        <h2>Format-Zeitleiste</h2>
        <div className="page-subtitle">
          Versionsfolge eines Formats über alle Formatversionen (Zeitscheiben)
          mit Gültigkeitszeitraum und Veröffentlichungsart.
        </div>
      </div>

      {!hasTimeline && (
        <Notice tone="info">
          Keine Zeitleisten-Daten verfügbar – bitte <code>npm run data</code> ausführen.
        </Notice>
      )}

      {hasTimeline && (
        <>
          <Card>
            <Flex className="ztl-toolbar" wrap="Wrap" alignItems="Center">
              <Flex className="ztl-field" alignItems="Center">
                <label>Doctype</label>
                <SegmentedControl
                  aria-label="Doctype"
                  selectedKeys={new Set([doctype])}
                  onSelectionChange={handleDoctypeChange}
                >
                  <Segment id="ahb">AHB</Segment>
                  <Segment id="mig">MIG</Segment>
                </SegmentedControl>
              </Flex>

              <Flex className="ztl-field" alignItems="Center">
                <label htmlFor="ztl-format">Format</label>
                <Select
                  id="ztl-format"
                  style={{ minWidth: "180px" }}
                  isDisabled={formats.length === 0}
                  selectedKey={effectiveFormat || null}
                  onSelectionChange={handleFormatChange}
                >
                  {formats.length === 0
                    ? [<SelectItem key="__empty" id="__empty">—</SelectItem>]
                    : formats.map((f) => (
                        <SelectItem key={f} id={f}>
                          {f}
                        </SelectItem>
                      ))}
                </Select>
              </Flex>

              <div className="ztl-spacer" />

              {entries.length > 0 && (
                <span className="ztl-entry-count">
                  {entries.length} Formatversion{entries.length !== 1 ? "en" : ""}
                </span>
              )}
            </Flex>
          </Card>

          {entries.length > 0 ? (
            <Card
              title={`${doctype.toUpperCase()} ${effectiveFormat}`}
              style={{ marginTop: "var(--s-4)" }}
            >
              <p style={{ margin: "0 0 var(--s-2)", color: "var(--prn-fg-2, inherit)", fontSize: "0.875em" }}>
                Zeitscheiben nach Formatversion
              </p>
              <div className="ztl-timeline">
                {entries.map((entry, i) => (
                  <TimelineRow
                    key={entry.fv}
                    entry={entry}
                    isLast={i === entries.length - 1}
                    doctype={doctype}
                    format={effectiveFormat}
                    current={entry.fv === release}
                  />
                ))}
              </div>
            </Card>
          ) : (
            <div style={{ marginTop: "var(--s-3)" }}>
              <Notice tone="info">Wähle Doctype und Format aus.</Notice>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default FormatZeitleistePage;
